import * as Sentry from "@sentry/nextjs";
import clarity from "@microsoft/clarity";

import { postErrorMessage } from "@/app/_actions/post-error";

import { getUserFromSession } from "@/helper/user/client";

import { today } from "@/constants";

export interface HandleErrorOptions {
  error: Error & {
    digest?: string;
    cause?: unknown;
    name?: string;
  };
  pathname: string;
  errorType: "GlobalError" | "ClientError";
}

export const handleError = async ({
  error,
  pathname,
  errorType,
}: HandleErrorOptions) => {
  const user = getUserFromSession();
  const userId = user?.id;

  // Log error to console with additional context
  console.error(
    errorType === "GlobalError" ? "Global Error:" : "Client Error:",
    {
      message: error.message,
      cause: error.cause,
      pathname,
      timestamp: today.toISOString(),
    }
  );

  // Generate a unique error ID that we'll use to correlate with Clarity
  const errorId = `${Date.now()}-${Math.random().toString(36).substring(2)}`;

  // Tag this error in Clarity
  try {
    if (typeof window !== "undefined") {
      clarity.setTag("errorId", errorId);
      if (userId) {
        clarity.identify(userId, undefined, undefined, "User Error");
      }
      clarity.event("error_occurred");
    }
  } catch (e) {
    console.error("Erro ao marcar tag de erro no Clarity:", e);
  }

  let sentryErrorUrl: string | undefined;

  // First try to capture in Sentry
  try {
    // Set up Sentry context and capture exception
    await Sentry.withScope(async (scope) => {
      if (errorType === "GlobalError") {
        scope.setExtra("errorType", "global");
      }

      scope.setExtra("pathname", pathname);
      scope.setExtra("timestamp", today.toISOString());
      scope.setExtra("userAgent", window.navigator.userAgent);
      scope.setExtra("platform", window.navigator.platform);
      scope.setExtra("vendor", window.navigator.vendor);
      scope.setExtra("clarityErrorId", errorId);
      scope.setTag("clarityErrorId", errorId);

      if (userId) {
        scope.setExtra("userId", userId);
        scope.setTag("userId", userId);
      }

      // Capture the error and wait for it to be sent
      const eventId = Sentry.captureException(error);

      console.warn("eventId", eventId);

      // Flush after capturing
      await Sentry.flush();

      // Construct Sentry URL using the organization and project
      sentryErrorUrl = `https://proleilao.sentry.io/issues/?project=4508112573562880&query=is%3Aunresolved%20clarityErrorId%3A${errorId}&referrer=issue-list&statsPeriod=14d&stream_index=0`;
    });
  } catch (sentryError) {
    console.error("Erro ao capturar erro no Sentry:", sentryError);
  }

  // Always post to our error tracking system, regardless of Sentry's status
  try {
    await postErrorMessage({
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      path: pathname,
      userAgent: window.navigator.userAgent,
      timestamp: today.toISOString(),
      type: errorType === "GlobalError" ? errorType : error.name,
      cause: error.cause,
      sentryErrorUrl,
      claritySessionId: errorId,
      userId,
    });
  } catch (telegramError) {
    console.error(
      "Erro ao enviar mensagem de erro para Telegram:",
      telegramError
    );
  }
};
